import React, { useRef, useState } from 'react'

import { Modal, Paper, Slide, Tab, Tabs } from '@material-ui/core'

import { AreaConfig, UnitConfig, ListVendor, Service } from '../../../models'
import { WorkforceFormState } from '../Workforce'
import { Container } from '../../../components'
import { EditCurrentPricing } from './EditCurrentPricing'
import { PricingTab } from './VendorTab/PricingTab'
import { MarketAnalysisTool } from '../../MarketAnalysisTool/MarketAnalysisTool'
import { UnsavedPricingChangesDialog } from './UnsavedPricingChangesDialog'

interface Props {
    vendor: ListVendor
    formState: WorkforceFormState
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    serviceList: Service[]
    onClickRequestUpdatedPricing: (vendor: ListVendor) => void
    handleClose: () => void
}

export const VendorPricingModal = (props: Props) => {
    const {
        formState,
        vendor,
        unitConfigs,
        areaConfigs,
        serviceList,
        onClickRequestUpdatedPricing,
    } = props
    const { open } = formState.modal.vendorPricing

    const [selectedTab, setSelectedTab] = useState(0)
    const [isDirty, setIsDirty] = useState(false)
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const saveRef = useRef<(() => Promise<void>) | null>(null)

    const closeModal = () => {
        setConfirmCloseOpen(false)
        setIsDirty(false)
        props.handleClose()
    }

    const requestClose = () => {
        if (isDirty) {
            setConfirmCloseOpen(true)
            return
        }
        closeModal()
    }

    const saveAndClose = async () => {
        if (!saveRef.current) {
            closeModal()
            return
        }
        setIsSaving(true)
        try {
            await saveRef.current()
            closeModal()
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <>
            <Modal
                open={open}
                onClose={requestClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                    <Paper
                        style={{
                            width: '80%',
                            height: '90%',
                        }}
                    >
                        {/* Main Container */}
                        <Container
                            direction="row"
                            style={{
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            {/* Main Content */}
                            <Container
                                direction="column"
                                style={{
                                    height: '100%',
                                    flex: 1,
                                }}
                            >
                                {/* Tabs */}
                                <Tabs
                                    value={selectedTab}
                                    onChange={(e, value) =>
                                        setSelectedTab(value)
                                    }
                                >
                                    <Tab label="Pricing" />
                                    <Tab label="Edit Pricing" />
                                    <Tab label="Market Rate Analysis" />
                                </Tabs>
                                {/* Tab Content */}
                                <Container
                                    style={{
                                        height: '100%',
                                        width: '100%',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {/* Pricing Tab */}
                                    {selectedTab === 0 && (
                                        <PricingTab
                                            vendor={vendor}
                                            unitConfigs={unitConfigs}
                                            areaConfigs={areaConfigs}
                                            onClickRequestUpdatedPricing={
                                                onClickRequestUpdatedPricing
                                            }
                                        />
                                    )}
                                    {/* Keep editor mounted so unsaved edits survive tab switches */}
                                    <div
                                        style={{
                                            display:
                                                selectedTab === 1
                                                    ? 'block'
                                                    : 'none',
                                            width: '100%',
                                        }}
                                    >
                                        <EditCurrentPricing
                                            vendor={vendor}
                                            unitConfigs={unitConfigs}
                                            areaConfigs={areaConfigs}
                                            onDirtyChange={setIsDirty}
                                            saveRef={saveRef}
                                        />
                                    </div>

                                    {/* Market Rate Analysis */}
                                    {selectedTab === 2 && (
                                        <MarketAnalysisTool
                                            embedded
                                            serviceList={serviceList}
                                        />
                                    )}
                                </Container>
                            </Container>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>
            <UnsavedPricingChangesDialog
                open={confirmCloseOpen}
                isSaving={isSaving}
                onCancel={() => setConfirmCloseOpen(false)}
                onDiscard={closeModal}
                onSave={saveAndClose}
            />
        </>
    )
}
